import type React from 'react';
import { useEffect } from 'react';
import { useRouter } from 'next/router';
import { getRedirectPath, useRentReportingAuth } from '../contexts/RentReportingAuthProvider';
import type { ClickstreamAuthTriggerSource } from '../analytics/events';
import { emitAuthCompleteEvent } from '../analytics/events';
import { ReportingForAllEnrollmentStatus } from '@zg-rentals/ts-rent-credit-report-client';

interface EnrollmentRedirectHandlerProps {
  children: React.ReactNode;
}

export const EnrollmentRedirectHandler: React.FC<EnrollmentRedirectHandlerProps> = ({ children }) => {
  const router = useRouter();
  const { user, updateEnrollmentStatus } = useRentReportingAuth();

  useEffect(() => {
    const cleanUpUrlParams = () => {
      const { auth_trigger: _auth_trigger, post_login_action: _post_login_action, ...restQuery } = router.query;
      router.replace(
        {
          pathname: router.pathname,
          query: restQuery,
        },
        undefined,
        { shallow: true },
      );
    };

    const enrollmentRedirect = async () => {
      if (user === null) {
        return;
      }

      // Check if auth was just completed by looking for auth_trigger query param
      const authTrigger = router.query.auth_trigger as string | undefined;
      const postLoginAction = router.query.post_login_action as string | undefined;
      if (authTrigger && user.isLoggedIn) {
        emitAuthCompleteEvent(authTrigger as ClickstreamAuthTriggerSource);

        // Handle post-login actions
        if (postLoginAction === 'initiate_enrollment' && !user.hasInitiatedEnrollment) {
          await updateEnrollmentStatus(ReportingForAllEnrollmentStatus.INITIATED_ENROLLMENT);
          cleanUpUrlParams();
          return;
        }
        cleanUpUrlParams();
      }

      const currentPath = router.pathname;
      const queryParams = router.query;
      const redirectPath = getRedirectPath(currentPath, queryParams as Record<string, string>, user);

      if (redirectPath) {
        router.replace(redirectPath);
      }
    };

    enrollmentRedirect();
  }, [user, router, updateEnrollmentStatus]);

  return <>{children}</>;
};
